import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FaWhatsapp, FaTelegramPlane, FaGlobe, FaSyncAlt, FaArrowLeft } from 'react-icons/fa';
import Btn from '../../../components/ui/Btn';
import StatusTag from '../../../components/ui/StatusTag';
import { agentsService, friendlyError } from '../../../services/agentsService';
import DataView from '../../Tools/editor/DataView';
import { STATUS_FILTERS, CHANNEL_FILTERS, PERIODS, periodSince, readFilters, writeFilters, hasFilters, describeFilters, countsFor } from '../../../utils/activityFilters';
import { diagnose } from '../../../utils/traceDiagnosis';
import { toastSuccess, toastError } from '../../../utils/toast';

// Actividad: cada mensaje que atendió el motor, con lo que entró, el contexto, cada vuelta del modelo, las herramientas y lo que
// respondió. Lista a la izquierda y detalle a la derecha (en pantallas chicas, uno a la vez). La traza abierta va en la dirección
// (?t=123) para poder compartirla con otra persona del equipo.

const CHANNEL = { whatsapp: { label: 'WhatsApp', icon: FaWhatsapp }, telegram: { label: 'Telegram', icon: FaTelegramPlane }, web: { label: 'Chat web', icon: FaGlobe } };

const RETENTION = [1, 3, 7, 14, 30, 90];
const STATUS = {
  ok: { tone: 'live', label: 'Respondió', dot: 'bg-live' },
  ignored: { tone: undefined, label: 'No respondió', dot: 'bg-fg-faint' },
  error: { tone: 'caution', label: 'Falló', dot: 'bg-caution' },
};

const ms = (n) => (n == null ? '—' : n < 1000 ? `${n} ms` : `${(n / 1000).toFixed(1)} s`);
const money = (n) => (n == null ? 'sin costo' : `US$${Number(n).toFixed(Number(n) < 0.01 ? 5 : 3)}`);
const time = (iso) => new Date(iso).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
const day = (iso) => {
  const d = new Date(iso); const today = new Date(); const y = new Date(); y.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Hoy';
  if (d.toDateString() === y.toDateString()) return 'Ayer';
  return d.toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' });
};
// Quién escribió, sin repetir el canal (el canal se muestra aparte, con su nombre).
const who = (t) => (t.channel === 'web' ? `Visitante ${String(t.chat_id || '').slice(2, 8)}` : String(t.chat_id || '').replace(/^tg:/, '') || 'sin dato');
const FINISH = { stop: 'terminó normal', tool_calls: 'pidió herramientas', length: 'se cortó por límite de tokens', content_filter: 'filtrado por OpenAI' };
const secs = (n) => `+${(n / 1000).toFixed(1)} s`;
const pretty = (s) => { try { return JSON.stringify(JSON.parse(s), null, 2); } catch { return s; } };

export default function ActivityPanel({ agentId }) {
  const [params, setParams] = useSearchParams();
  const selected = Number(params.get('t')) || null;
  const filters = useMemo(() => readFilters(params), [params]);
  const [items, setItems] = useState([]);
  const [state, setState] = useState('loading');       // loading | ready | error | missing
  const [error, setError] = useState('');
  const [more, setMore] = useState(false);
  const [retention, setRetention] = useState(null);
  const [rawCounts, setRawCounts] = useState(null);     // filas agrupadas de agent_trace_counts (script 44); null = sin contadores
  const [tick, setTick] = useState(0);

  const load = useCallback(async (append = false, from = null) => {
    if (!append) setState('loading');
    try {
      const rows = await agentsService.listTraces(agentId, { status: filters.status || undefined, channel: filters.channel || undefined, since: periodSince(filters.period), before: from });
      setItems((cur) => (append ? [...cur, ...rows] : rows));
      setMore(rows.length === 40);
      setState('ready');
    } catch (e) {
      if (/agent_traces|schema cache|does not exist/i.test(e.message)) { setState('missing'); return; }
      setError(friendlyError(e)); setState('error');
    }
  }, [agentId, filters]);

  useEffect(() => { load(); }, [load]);
  // Contadores: una consulta agrupada por periodo; al cambiar resultado o canal solo se recalculan aquí, sin volver a pedir nada.
  useEffect(() => {
    let live = true;
    agentsService.traceCounts(agentId, periodSince(filters.period)).then((r) => live && setRawCounts(r)).catch(() => live && setRawCounts(null));
    return () => { live = false; };
  }, [agentId, filters.period, tick]);
  const counts = useMemo(() => (rawCounts ? countsFor(rawCounts, filters) : null), [rawCounts, filters]);
  useEffect(() => {
    agentsService.traceSettings(agentId).then((s) => setRetention(s.retentionDays)).catch((e) => { if (e.code === 'missing_36') setState('missing'); });
  }, [agentId]);

  // Abrir una interacción conserva los filtros (para volver a la misma vista); cambiar un filtro cierra la que estaba abierta.
  const open = (id) => { const next = new URLSearchParams(params); if (id) next.set('t', String(id)); else next.delete('t'); setParams(next, { replace: false }); };
  const setFilters = (patch) => setParams(writeFilters(params, { ...filters, ...patch }), { replace: true });

  if (state === 'missing') {
    return (
      <p role="note" className="rounded-xl border border-caution/30 bg-caution-soft px-4 py-3 text-[13.5px] text-fg max-w-[70ch]">
        Para ver la actividad hay que correr el script <code>36_agent_traces.sql</code> en Supabase y desplegar el motor. Desde entonces, cada mensaje que atienda este agente aparecerá aquí.
      </p>
    );
  }

  return (
    <div>
      <div className="mb-4 space-y-3">
        <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
          <FilterGroup label="Resultado" options={STATUS_FILTERS} value={filters.status} counts={counts?.status} onChange={(status) => setFilters({ status })} />
          <FilterGroup label="Canal" options={CHANNEL_FILTERS} value={filters.channel} counts={counts?.channel} onChange={(channel) => setFilters({ channel })} />
          <div className="flex items-center gap-2">
            <label htmlFor="periodo" className="text-[12.5px] text-fg-faint">Periodo</label>
            <select id="periodo" value={filters.period} onChange={(e) => setFilters({ period: e.target.value })}
              className="h-8 rounded-full bg-raised ring-1 ring-line px-3 text-[13px] text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/60">
              {PERIODS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Btn size="sm" variant="ghost" onClick={() => { load(); setTick((t) => t + 1); }} aria-label="Actualizar la lista"><FaSyncAlt aria-hidden className="text-[11px]" /><span>Actualizar</span></Btn>
          {hasFilters(filters) && <Btn size="sm" variant="ghost" onClick={() => setFilters({ status: '', channel: '', period: '' })}>Quitar filtros</Btn>}
          <RetentionPicker agentId={agentId} value={retention} onChange={setRetention} />
        </div>
      </div>

      <div className="grid lg:grid-cols-[minmax(0,340px)_minmax(0,1fr)] gap-5 items-start">
        <div className={selected ? 'hidden lg:block' : ''}>
          <TraceList items={items} state={state} error={error} selected={selected} onOpen={open} retention={retention} filtered={hasFilters(filters)} describe={describeFilters(filters)} onClear={() => setFilters({ status: '', channel: '', period: '' })}
            onMore={more ? () => load(true, items[items.length - 1]?.id) : null} />
        </div>
        <div className={selected ? '' : 'hidden lg:block'}>
          {selected ? <TraceDetail id={selected} onBack={() => open(null)} /> : (
            <div className="rounded-2xl border border-dashed border-fg/15 p-8 text-center text-[13.5px] text-fg-dim">Elige una interacción para ver paso a paso qué hizo el agente.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function FilterGroup({ label, options, value, onChange, counts }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-[12.5px] text-fg-faint" aria-hidden>{label}</span>
      <div role="group" aria-label={`Filtrar por ${label.toLowerCase()}`} className="inline-flex flex-wrap gap-1 rounded-2xl sm:rounded-full bg-raised p-1 ring-1 ring-inset ring-line">
        {options.map((f) => (
          <button key={f.key} type="button" aria-pressed={value === f.key} onClick={() => onChange(f.key)}
            className={`h-8 px-3.5 rounded-full text-[13px] font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/60 ${value === f.key ? 'bg-panel text-fg shadow-sm ring-1 ring-line' : 'text-fg-dim hover:text-fg'}`}>
            {f.label}
            {counts && <span className={`ml-1.5 text-[11.5px] tabular-nums ${counts[f.key] ? 'opacity-70' : 'opacity-40'}`}>{(counts[f.key] ?? 0).toLocaleString('es-MX')}</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

function RetentionPicker({ agentId, value, onChange }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  if (value === null) return null;
  const save = async (days) => {
    setBusy(true); setError('');
    try { await agentsService.setTraceRetention(agentId, days); onChange(days); } catch (e) { setError(friendlyError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="ml-auto flex items-center gap-2 text-[13px] text-fg-dim">
      <label htmlFor="retencion">Guardar la actividad</label>
      <select id="retencion" value={value} disabled={busy} onChange={(e) => save(Number(e.target.value))}
        className="h-8 rounded-full bg-raised ring-1 ring-line px-3 text-[13px] text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/60">
        {RETENTION.map((d) => <option key={d} value={d}>{d === 1 ? '1 día' : `${d} días`}</option>)}
        <option value={0}>No guardar</option>
      </select>
      {error && <span role="alert" className="text-signal">{error}</span>}
    </div>
  );
}

function TraceList({ items, state, error, selected, onOpen, onMore, retention, filtered, describe, onClear }) {
  const groups = useMemo(() => {
    const out = [];
    for (const t of items) {
      const d = day(t.created_at);
      if (!out.length || out[out.length - 1].day !== d) out.push({ day: d, items: [] });
      out[out.length - 1].items.push(t);
    }
    return out;
  }, [items]);

  if (state === 'loading') return <div aria-busy="true" className="space-y-2">{[0, 1, 2, 3].map((i) => <div key={i} className="h-16 skeleton rounded-xl" />)}</div>;
  if (state === 'error') return <p role="alert" className="text-[13.5px] text-signal">{error}</p>;
  if (!items.length) {
    return (
      <div className="rounded-2xl ring-1 ring-line p-6 text-[13.5px] text-fg-dim">
        {filtered ? <>No hay interacciones con estos filtros: <b className="text-fg">{describe}</b>.<span className="mt-3 block"><Btn size="sm" variant="quiet" onClick={onClear}>Quitar filtros</Btn></span></> : retention === 0 ? 'La actividad está apagada para este agente: elige cuántos días guardarla.' : 'Todavía no hay actividad. Escríbele por el chat web o por un canal conectado y aparecerá aquí.'}
      </div>
    );
  }
  return (
    <div className="rounded-2xl bg-panel ring-1 ring-line overflow-hidden">
      {groups.map((g) => (
        <section key={g.day} aria-label={g.day}>
          <h3 className="sticky top-0 z-[1] px-4 py-2 text-[12px] font-semibold text-fg-faint bg-panel/95 backdrop-blur-sm border-b border-line first-letter:uppercase">{g.day}</h3>
          <ul>
            {g.items.map((t) => {
              const Ch = CHANNEL[t.channel]?.icon || FaGlobe;
              const st = STATUS[t.status] || STATUS.ok;
              const active = t.id === selected;
              return (
                <li key={t.id} className="border-b border-line last:border-0">
                  <button type="button" onClick={() => onOpen(t.id)} aria-current={active ? 'true' : undefined}
                    className={`w-full text-left px-4 py-3 flex gap-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-live/60 ${active ? 'bg-raised' : 'hover:bg-raised/50'}`}>
                    <span className="relative mt-0.5 grid place-items-center h-8 w-8 shrink-0 rounded-full bg-raised text-fg-dim">
                      <Ch aria-hidden className="text-[13px]" />
                      <span aria-hidden className={`absolute -right-0.5 -bottom-0.5 h-2.5 w-2.5 rounded-full ring-2 ring-panel ${st.dot}`} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-baseline gap-2">
                        <span className="truncate text-[13.5px] font-medium text-fg">{t.input || '(sin texto)'}</span>
                        <span className="ml-auto shrink-0 text-[12px] text-fg-faint tabular-nums">{time(t.created_at)}</span>
                      </span>
                      <span className="mt-0.5 block truncate text-[12.5px] text-fg-dim">
                        {t.status === 'ok' ? t.output : <span className={t.status === 'error' ? 'text-caution' : ''}>{st.label}{t.reason ? `: ${t.reason}` : ''}</span>}
                      </span>
                      <span className="mt-1 block text-[11.5px] text-fg-faint tabular-nums"><span className="font-medium text-fg-dim">{CHANNEL[t.channel]?.label || t.channel}</span> · {who(t)} · {ms(t.latency_ms)}{t.cost_usd != null ? ` · ${money(t.cost_usd)}` : ''}</span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
      {onMore && <div className="p-3 text-center border-t border-line"><Btn size="sm" variant="ghost" onClick={onMore}>Ver anteriores</Btn></div>}
    </div>
  );
}

function TraceDetail({ id, onBack }) {
  const [t, setT] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let live = true;
    setT(null); setError('');
    agentsService.getTrace(id).then((x) => { if (live) { if (x) setT(x); else setError('Esa interacción ya no existe: se borró al cumplir sus días de guardado.'); } })
      .catch((e) => live && setError(friendlyError(e)));
    return () => { live = false; };
  }, [id]);

  if (error) return <div><BackLink onBack={onBack} /><p role="alert" className="mt-3 text-[13.5px] text-signal">{error}</p></div>;
  if (!t) return <div aria-busy="true" className="h-96 skeleton rounded-2xl" />;
  const st = STATUS[t.status] || STATUS.ok;
  const ch = CHANNEL[t.channel]?.label || t.channel;
  const steps = Array.isArray(t.steps) ? t.steps : [];
  const findings = diagnose(t);
  // Cada herramienta del modelo recibe su resultado en la vuelta siguiente: se anota para seguir el hilo dato por dato.
  let lastRound = 0;
  const view = steps.map((x) => { if (x.kind === 'model') lastRound = x.round; return x.kind === 'tool' && !x.pre ? { ...x, _round: lastRound } : x; });
  const copyAll = async () => {
    try { await navigator.clipboard.writeText(JSON.stringify(t, null, 2)); toastSuccess('Traza copiada'); } catch { toastError('No se pudo copiar'); }
  };

  return (
    <article aria-label="Detalle de la interacción" className="rounded-2xl bg-panel ring-1 ring-line p-5 sm:p-6">
      <BackLink onBack={onBack} className="lg:hidden mb-3" />
      <header className="flex flex-wrap items-center gap-2">
        <StatusTag tone={st.tone} dot>{st.label}</StatusTag>
        <span className="text-[13px] text-fg-dim">{new Date(t.created_at).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'medium' })} · {ch} · {who(t)}</span>
        <Btn size="sm" variant="ghost" className="ml-auto" onClick={copyAll}>Copiar traza completa</Btn>
      </header>
      <dl className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Fact label="Tardó">{ms(t.latency_ms)}</Fact>
        <Fact label="Tokens">{t.prompt_tokens != null ? `${t.prompt_tokens.toLocaleString('es-MX')} + ${t.completion_tokens.toLocaleString('es-MX')}` : '—'}</Fact>
        <Fact label="Costo">{money(t.cost_usd)}</Fact>
        <Fact label="Versión">{t.agent_version ? `v${t.agent_version}` : '—'}{t.attempt > 1 ? ` · intento ${t.attempt}` : ''}</Fact>
      </dl>
      <Review findings={findings} />
      {t.reason && t.status !== 'ok' && <p className={`mt-4 rounded-lg px-3 py-2 text-[13px] ${t.status === 'error' ? 'bg-caution-soft text-fg' : 'bg-raised text-fg-dim'}`}>{t.reason}</p>}

      <ol className="mt-6 relative">
        <Step title="Entró un mensaje" meta={ch} tone="in">
          <Bubble side="in">{t.input || '(sin texto)'}</Bubble>
        </Step>
        {view.map((s, i) => <StepView key={i} s={s} channel={ch} output={t.output} />)}
      </ol>
    </article>
  );
}

const LEVEL = {
  error: { label: 'Falla', bar: 'bg-signal', text: 'text-signal' },
  warn: { label: 'Revisar', bar: 'bg-caution', text: 'text-caution' },
  info: { label: 'Dato', bar: 'bg-fg-faint', text: 'text-fg-faint' },
};

function Review({ findings }) {
  if (!findings.length) {
    return <p className="mt-4 rounded-lg bg-raised px-3 py-2 text-[13px] text-fg-dim">La revisión automática no encontró nada raro. Eso no garantiza que la respuesta sea correcta: compárala con lo que devolvieron las herramientas.</p>;
  }
  return (
    <section aria-label="Revisión automática" className="mt-4 rounded-xl ring-1 ring-line">
      <h3 className="px-3 pt-3 text-[13px] font-semibold text-fg">Qué mirar primero</h3>
      <ul className="p-2 space-y-1">
        {findings.map((f, i) => {
          const l = LEVEL[f.level];
          return (
            <li key={i} className="flex gap-3 rounded-lg px-2 py-2">
              <span aria-hidden className={`mt-1 h-4 w-1 shrink-0 rounded-full ${l.bar}`} />
              <div className="min-w-0 text-[13px]">
                <p className="font-semibold text-fg"><span className={`mr-2 text-[12px] font-semibold ${l.text}`}>{l.label}</span>{f.title}</p>
                <p className="mt-0.5 text-fg-dim break-words">{f.detail}</p>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

const BackLink = ({ onBack, className = '' }) => (
  <button type="button" onClick={onBack} className={`inline-flex items-center gap-2 text-[13px] text-fg-dim hover:text-fg rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/60 ${className}`}>
    <FaArrowLeft aria-hidden className="h-3 w-3" />Volver a la lista
  </button>
);

const Fact = ({ label, children }) => (
  <div className="rounded-xl bg-raised/60 px-3 py-2">
    <dt className="text-[11.5px] text-fg-faint">{label}</dt>
    <dd className="text-[14px] font-semibold text-fg tabular-nums">{children}</dd>
  </div>
);

function Step({ title, meta, tone, at, children }) {
  const dot = { in: 'bg-fg', model: 'bg-[#7C5CFF]', tool: 'bg-caution', out: 'bg-live', error: 'bg-signal', muted: 'bg-fg-faint' }[tone] || 'bg-fg-faint';
  return (
    <li className="relative pl-7 pb-5 last:pb-0 before:absolute before:left-[5px] before:top-3 before:bottom-0 before:w-px before:bg-line last:before:hidden">
      <span aria-hidden className={`absolute left-0 top-[5px] h-[11px] w-[11px] rounded-full ring-4 ring-panel ${dot}`} />
      <p className="text-[13.5px] font-semibold text-fg">{title}{meta && <span className="ml-2 font-normal text-[12.5px] text-fg-faint tabular-nums">{meta}</span>}{at != null && <span className="ml-2 font-normal text-[12px] text-fg-faint tabular-nums">{secs(at)}</span>}</p>
      {children && <div className="mt-2">{children}</div>}
    </li>
  );
}

const Bubble = ({ side, children }) => (
  <p className={`inline-block max-w-full whitespace-pre-wrap break-words rounded-2xl px-3.5 py-2 text-[13.5px] leading-relaxed ${side === 'in' ? 'bg-raised text-fg rounded-tl-md' : 'bg-live-soft text-fg rounded-tl-md'}`}>{children}</p>
);

function Fold({ summary, children }) {
  return (
    <details className="group rounded-lg ring-1 ring-line">
      <summary className="cursor-pointer list-none px-3 py-2 text-[12.5px] text-fg-dim hover:text-fg flex items-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/60 rounded-lg [&::-webkit-details-marker]:hidden">
        <span aria-hidden className="transition-transform group-open:rotate-90">›</span>{summary}
      </summary>
      <div className="px-3 pb-3">{children}</div>
    </details>
  );
}
const Pre = ({ children }) => <pre className="max-h-80 overflow-auto whitespace-pre-wrap break-words rounded-md bg-ink p-3 text-[12px] leading-relaxed text-fg-dim font-mono">{children}</pre>;

function StepView({ s, channel, output }) {
  if (s.kind === 'context') {
    const used = (s.knowledge || []).filter((k) => k.used).length;
    return (
      <Step title="Armó el contexto" meta={`${ms(s.ms)} · ${s.history?.length === 1 ? '1 mensaje' : `${s.history?.length || 0} mensajes`} de historial${s.knowledge?.length ? ` · ${used} de ${s.knowledge.length} datos de conocimiento` : ''}${s.memory?.length ? ` · ${s.memory.length === 1 ? '1 recuerdo' : `${s.memory.length} recuerdos`}` : ''}`} tone="muted" at={s.at}>
        <div className="space-y-2">
          {s.params && (
            <p className="text-[12.5px] text-fg-dim">
              Modelo <code className="rounded bg-raised px-1.5 py-0.5 text-[12px] text-fg">{s.params.model}</code> · hasta {s.params.maxTokens} tokens de respuesta · {s.params.temperature == null ? 'temperatura por defecto' : `temperatura ${s.params.temperature}`}
              {' · '}{s.tools?.length ? <>herramientas ofrecidas: {s.tools.map((n) => <code key={n} className="mr-1 rounded bg-raised px-1.5 py-0.5 text-[12px] text-fg">{n}</code>)}</> : 'sin herramientas'}
            </p>
          )}
          {s.clipped && <p className="text-[12.5px] text-caution">Parte de este contexto se guardó recortada (el modelo sí la recibió completa).</p>}
          {s.preData && <Fold summary="Datos previos que entraron a las instrucciones"><Pre>{pretty(s.preData)}</Pre></Fold>}
          <Fold summary="Instrucciones que recibió el modelo (ya armadas con los datos)"><Pre>{s.system}</Pre></Fold>
          {s.history?.length > 0 && (
            <Fold summary={`Historial (${s.history.length})`}>
              <ul className="space-y-1.5 text-[12.5px]">{s.history.map((h, i) => <li key={i}><span className="text-fg-faint">{h.role === 'user' ? 'Persona' : 'Agente'}:</span> <span className="text-fg-dim">{h.text}</span></li>)}</ul>
            </Fold>
          )}
          {s.knowledge?.length > 0 && (
            <Fold summary="Conocimiento encontrado">
              <ul className="space-y-2 text-[12.5px]">
                {s.knowledge.map((k, i) => (
                  <li key={i} className={k.used ? 'text-fg-dim' : 'text-fg-faint'}>
                    <span className="font-mono tabular-nums">{k.score.toFixed(2)}</span> · {k.used ? 'se usó' : 'no se usó (poco parecido)'}<br />{k.text}
                  </li>
                ))}
              </ul>
            </Fold>
          )}
          {s.memory?.length > 0 && <Fold summary="Lo que recordaba de la persona"><ul className="list-disc pl-4 text-[12.5px] text-fg-dim">{s.memory.map((m, i) => <li key={i}>{m}</li>)}</ul></Fold>}
        </div>
      </Step>
    );
  }
  if (s.kind === 'model') {
    return (
      <Step title={s.toolCalls?.length ? `El modelo decidió usar ${s.toolCalls.length === 1 ? 'una herramienta' : `${s.toolCalls.length} herramientas`}` : 'El modelo redactó la respuesta'}
        meta={`vuelta ${s.round} · ${ms(s.ms)} · ${s.prompt} + ${s.completion} tokens`} tone="model" at={s.at}>
        {s.finish && <p className={`mb-1.5 text-[12.5px] ${s.finish === 'length' || s.finish === 'content_filter' ? 'text-caution' : 'text-fg-faint'}`}>Motivo de cierre: {FINISH[s.finish] || s.finish}{s.messages ? ` · recibió ${s.messages} mensajes` : ''}</p>}
        {s.toolCalls?.length > 0 && (
          <ul className="space-y-1.5">{s.toolCalls.map((c, i) => (
            <li key={i}><code className="rounded-md bg-raised px-2 py-0.5 text-[12px] text-fg">{c.name}</code>
              <Fold summary="Datos con los que la pidió"><Pre>{pretty(c.args) || '{}'}</Pre></Fold></li>
          ))}</ul>
        )}
        {s.content && s.content !== output && <Fold summary="Texto del modelo"><Pre>{s.content}</Pre></Fold>}
      </Step>
    );
  }
  if (s.kind === 'tool') {
    return (
      <Step title={s.pre ? `Datos previos: ${s.name.replace(/^antes de responder: /, '')}` : `Herramienta ${s.name}`} meta={`${ms(s.ms)} · ${s.ok ? 'funcionó' : 'falló'}`} tone={s.ok ? 'tool' : 'error'} at={s.at}>
        <div className="grid sm:grid-cols-2 gap-2">
          <div><p className="mb-1 text-[11.5px] text-fg-faint">Datos con los que se llamó</p><Pre>{pretty(s.args) || '{}'}</Pre></div>
          <div><p className="mb-1 text-[11.5px] text-fg-faint">Lo que devolvió{s.clipped ? ' (guardado recortado)' : ''}</p><Pre>{s.output}</Pre></div>
        </div>
        {s.fedToModel && <p className="mt-2 text-[12.5px] text-fg-faint">El modelo recibió esta respuesta en la vuelta {s._round + 1}{s.ok ? '.' : ' y tuvo que decidir qué decir sin el dato.'}</p>}
        {s.pre && <p className="mt-2 text-[12.5px] text-fg-faint">{s.ok ? 'Este resultado quedó disponible en las instrucciones antes de llamar al modelo.' : 'Falló: el agente respondió sin estos datos.'}</p>}
        {s.error && <p className="mt-2 text-[12.5px] text-signal">{s.error}</p>}
        {Array.isArray(s.nodes) && s.nodes.length > 0 && (
          <ol className="mt-3 space-y-2" aria-label="Pasos de la herramienta">
            {s.nodes.map((n) => (
              <li key={n.id} className="rounded-xl ring-1 ring-line">
                <details>
                  <summary className="cursor-pointer list-none flex flex-wrap items-center gap-2 px-3 py-2 text-[12.5px] [&::-webkit-details-marker]:hidden">
                    <span className={`h-2 w-2 rounded-full ${n.ok ? 'bg-live' : 'bg-signal'}`} aria-hidden />
                    <span className="font-semibold text-fg">{n.name || n.id}</span>
                    <span className="text-fg-faint tabular-nums">{ms(n.ms)}{n.status ? ` · ${n.status}` : ''}{n.branch ? ` · camino «${n.branch === 'then' ? 'Sí' : 'No'}»` : ''}</span>
                    {n.error && <span className="basis-full text-signal">{n.error}</span>}
                  </summary>
                  <div className="grid md:grid-cols-2 gap-3 px-3 pb-3">
                    <div><p className="mb-1 text-[11.5px] font-semibold text-fg-faint">Entrada</p>{n.input !== undefined ? <DataView value={n.input} base={`nodos.${n.id}`} pickable={false} /> : <p className="text-[12px] text-fg-faint">{n.nota || 'Sin datos.'}</p>}</div>
                    <div><p className="mb-1 text-[11.5px] font-semibold text-fg-faint">Salida</p>{n.output !== undefined ? <DataView value={n.output} base={`nodos.${n.id}`} pickable={false} /> : <p className="text-[12px] text-fg-faint">{n.nota || n.result || 'Sin datos.'}</p>}</div>
                  </div>
                </details>
              </li>
            ))}
          </ol>
        )}
      </Step>
    );
  }
  if (s.kind === 'send') return <Step title={`Respondió por ${channel}`} meta={ms(s.ms)} tone="out" at={s.at}><Bubble side="out">{output}</Bubble></Step>;
  if (s.kind === 'note') return <Step title="Nota" tone="muted" at={s.at}><p className="text-[13px] text-fg-dim">{s.text}</p></Step>;
  if (s.kind === 'error') return <Step title="Falló" tone="error" at={s.at}><p className="text-[13px] text-signal break-words">{s.text}</p><p className="mt-1 text-[12px] text-fg-faint">Se deshizo todo lo de este intento; el motor lo reintenta solo hasta 3 veces.</p></Step>;
  return null;
}
